/** WebSocket 所需的最小接口；业务层不依赖连接位于父页还是桥接页。 */
export interface ImSocket {
  readonly readyState: number;
  onopen: (() => void) | null;
  onerror: ((error?: Error) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason?: string }) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export const IM_BRIDGE_PROTOCOL = 'dcs-im-ws-v1';
export const IM_SOCKET_TIMEOUT_MS = 12_000;

export class ImSocketError extends Error {
  constructor(message: string, public readonly retryable = true) {
    super(message);
    this.name = 'ImSocketError';
  }
}

/** 自有 H5/预览页直连；第三方网页由入口显式配置桥接地址，不依赖 UA 判断。 */
export function resolveImBridgeUrl(widgetBundleUrl: string, pageOrigin: string): string | undefined {
  const bundle = new URL(widgetBundleUrl);
  if (bundle.origin === pageOrigin) return undefined;
  return new URL('im-ws-bridge.html', bundle).toString();
}

function directSocket(url: string): ImSocket {
  const ws = new WebSocket(url);
  const socket: ImSocket = {
    get readyState() { return ws.readyState; },
    onopen: null,
    onerror: null,
    onmessage: null,
    onclose: null,
    send: (data) => ws.send(data),
    close: (code, reason) => ws.close(code, reason),
  };
  ws.onopen = () => socket.onopen?.();
  ws.onerror = () => socket.onerror?.(new ImSocketError('WebSocket connection failed'));
  ws.onmessage = (event) => socket.onmessage?.({ data: event.data });
  ws.onclose = (event) => socket.onclose?.({ code: event.code, reason: event.reason });
  return socket;
}

export class IframeImSocket implements ImSocket {
  readyState = 0;
  onopen: ImSocket['onopen'] = null;
  onerror: ImSocket['onerror'] = null;
  onmessage: ImSocket['onmessage'] = null;
  onclose: ImSocket['onclose'] = null;
  private readonly frame: HTMLIFrameElement;
  private readonly origin: string;
  private readonly channel = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  private readonly timeout: ReturnType<typeof setTimeout>;
  private connectSent = false;
  private loaded = false;

  constructor(private readonly wsUrl: string, bridgeUrl: string) {
    const url = new URL(bridgeUrl);
    if (!['https:', 'http:'].includes(url.protocol) || window.location.origin === 'null') {
      throw new ImSocketError('IM 桥接页需要有效的 HTTP(S) 来源', false);
    }
    this.origin = url.origin;
    // fragment 不会进入 HTTP 请求或访问日志；访客 token 仅在就绪后通过 postMessage 传递。
    url.hash = new URLSearchParams({ channel: this.channel, parentOrigin: window.location.origin }).toString();
    this.frame = document.createElement('iframe');
    this.frame.hidden = true;
    this.frame.style.display = 'none';
    this.frame.tabIndex = -1;
    this.frame.setAttribute('aria-hidden', 'true');
    this.frame.title = '客服连接';
    this.frame.referrerPolicy = 'no-referrer';
    this.frame.src = url.toString();
    this.frame.onload = () => {
      if (this.loaded) {
        this.fail(new ImSocketError('IM 桥接页已重新加载'));
      } else {
        this.loaded = true;
        this.post('hello');
      }
    };
    this.frame.onerror = () => this.fail(new ImSocketError('IM 桥接页加载失败'));
    window.addEventListener('message', this.receive);
    document.addEventListener('securitypolicyviolation', this.onPolicyViolation);
    this.timeout = setTimeout(() => {
      this.fail(new ImSocketError('IM 桥接连接超时，请检查桥接页是否允许加载'));
    }, IM_SOCKET_TIMEOUT_MS);
    (document.body ?? document.documentElement).appendChild(this.frame);
  }

  private post(type: string, payload: Record<string, unknown> = {}): void {
    this.frame.contentWindow?.postMessage({ protocol: IM_BRIDGE_PROTOCOL, channel: this.channel, type, ...payload }, this.origin);
  }

  private receive = (event: MessageEvent): void => {
    if (this.readyState >= 2 || event.origin !== this.origin || event.source !== this.frame.contentWindow) return;
    const data = event.data;
    if (!data || data.protocol !== IM_BRIDGE_PROTOCOL || data.channel !== this.channel) return;
    switch (data.type) {
      case 'ready':
        if (!this.connectSent) {
          this.connectSent = true;
          this.post('connect', { url: this.wsUrl });
        }
        break;
      case 'open':
        if (this.readyState !== 0 || !this.connectSent) return;
        clearTimeout(this.timeout);
        this.readyState = 1;
        this.onopen?.();
        break;
      case 'message':
        if (this.readyState === 1 && typeof data.data === 'string') this.onmessage?.({ data: data.data });
        break;
      case 'error':
        this.fail(new ImSocketError('IM 桥接 WebSocket 连接失败', data.retryable !== false));
        break;
      case 'close':
        this.finish(typeof data.code === 'number' ? data.code : 1006);
        break;
    }
  };

  private onPolicyViolation = (event: SecurityPolicyViolationEvent): void => {
    if (event.disposition === 'report') return;
    if (!['frame-src', 'child-src', 'default-src'].includes(event.effectiveDirective)) return;
    if (event.blockedURI === this.origin || event.blockedURI === this.frame.src.split('#')[0]) {
      this.fail(new ImSocketError('宿主安全策略禁止加载客服桥接页', false));
    }
  };

  send(data: string): void {
    if (this.readyState !== 1) throw new ImSocketError('IM 桥接连接尚未就绪');
    this.post('send', { data });
  }

  close(code = 1000, reason = ''): void {
    if (this.readyState >= 2) return;
    this.post('disconnect', { code, reason });
    this.finish(code);
  }

  private fail(error: ImSocketError): void {
    if (this.readyState >= 2) return;
    this.onerror?.(error);
    this.finish(1006);
  }

  private finish(code: number): void {
    if (this.readyState >= 2) return;
    this.readyState = 3;
    clearTimeout(this.timeout);
    window.removeEventListener('message', this.receive);
    document.removeEventListener('securitypolicyviolation', this.onPolicyViolation);
    this.frame.onload = null;
    this.frame.onerror = null;
    this.frame.remove();
    this.onclose?.({ code });
  }
}

export function createImSocket(url: string, bridgeUrl?: string): ImSocket {
  return bridgeUrl ? new IframeImSocket(url, bridgeUrl) : directSocket(url);
}
